import React, {
  useState,
  useEffect,
  ChangeEventHandler,
  FocusEventHandler,
  useCallback
} from 'react';
import { TextInput, Button } from 'carbon-components-react';
import { FormError } from 'components';
import AddCustomHeadersTextFields from './addCustomHeadersTextFields';

const WebHookConfig: React.FC<Props> = ({
  handleChange,
  handleBlur,
  values,
  errors,
  touched,
  customHeaderchangeHandler,
  secureKey,
  generateKeyHandler
}) => {
  const [showCustomHeaders, setShowCustomHeaders] = useState<boolean>(false);
  const inputError = useCallback(
    (key: string) => {
      if (errors[key] && touched[key]) {
        return <FormError>{errors[key]}</FormError>;
      }

      return null;
    },
    [errors, touched]
  );

  const generateKey = () => {
    const key = Math.random().toString(16).substr(2, 16);
    generateKeyHandler(key);
  };

  const updateSecretKey = (evt: any) => {
    generateKeyHandler(evt.target.value);
    evt.type === 'blur' ? handleBlur(evt) : handleChange(evt);
  };

  const handleCopySecretButton = () =>
    navigator.clipboard.writeText(values.clientKey);

  useEffect(() => {
    Array.isArray(values.customHeaders) &&
      values.customHeaders[0]?.name !== '' &&
      setShowCustomHeaders(true);
  }, [values]);

  return (
    <>
      <div className="form-section--item">
        <TextInput
          id="url"
          name="url"
          type="url"
          labelText="Web service URL"
          placeholder="https://..."
          onChange={handleChange}
          onBlur={handleBlur}
          defaultValue={values.url}
          data-testid="url"
          helperText="Requires secure https://"
        />
        {inputError('url')}
      </div>
      <div className="form-section--item">
        <div className="bx--form-item bx--text-input-wrapper">
          <div className="generateKey--container">
            <label className="generateKey--label bx--label">
              Signing secret key
            </label>
            <Button
              kind="ghost"
              className="generateKey--btn"
              onClick={() => generateKey()}
            >
              Generate key
            </Button>
          </div>
          <div className="bx--text-input__field-outer-wrapper">
            <div className="bx--text-input__field-wrapper">
              <TextInput
                id="clientKey"
                name="clientKey"
                labelText=""
                className="key-code-snippet-secret"
                onClick={handleCopySecretButton}
                onChange={(evt) => updateSecretKey(evt)}
                onBlur={(evt) => updateSecretKey(evt)}
                value={values.clientKey || secureKey}
                helperText="8 - 32 characters"
                data-testid="clientKey"
                maxLength={32}
              />
            </div>
          </div>
        </div>
        {inputError('clientKey')}
      </div>
      {showCustomHeaders ? (
        <AddCustomHeadersTextFields
          defaultValues={values.customHeaders}
          handleChangeValues={(value: any) => customHeaderchangeHandler(value)}
        />
      ) : (
        <Button
          className="custom-http-header--btn"
          kind="ghost"
          onClick={() => setShowCustomHeaders(true)}
        >
          Add a custom HTTP header (optional)
        </Button>
      )}
    </>
  );
};

type Props = {
  handleChange: ChangeEventHandler;
  handleBlur: FocusEventHandler;
  values: any;
  errors: any;
  touched: any;
  customHeaderchangeHandler: Function;
  secureKey: string;
  generateKeyHandler: Function;
};

WebHookConfig.displayName = 'WebHookConfigComponent';

export default React.memo(WebHookConfig);
